<template>
  <div class="upcoming-awards-table">
    <app-loading :loading="loading" />
    <template v-if="!loading">
      <!-- Summary stat -->
      <div v-if="awards.length > 0" class="upcoming-awards-table__summary">
        <div class="upcoming-awards-table__total">{{ formattedTotalValue }}</div>
        <div class="upcoming-awards-table__description">Due in the next 30 days</div>
      </div>

      <b-table
        v-if="awards.length > 0"
        :items="enrichedAwards"
        :fields="UPCOMING_AWARDS_FIELDS"
        hover
        responsive
        borderless
        show-empty
        class="upcoming-awards-table__table"
      >
        <template #cell(full_name)="{ item }">
          <div class="upcoming-awards-table__employee">
            <div class="upcoming-awards-table__avatar">
              <font-awesome-icon :icon="['fad', 'user-circle']" />
            </div>
            <span class="upcoming-awards-table__name">{{ item.full_name }}</span>
          </div>
        </template>

        <template #cell(work_email)="{ item }">
          <span class="upcoming-awards-table__email">{{ item.work_email }}</span>
        </template>

        <template #cell(employee_code)="{ item }">
          <span>{{ item.employee_code }}</span>
        </template>

        <template #cell(scope)="{ item }">
          <template v-if="item.divisionNames && item.divisionNames.length > 0">
            <span>{{ item.divisionNames[0] }}</span>
          </template>
          <template v-else>
            <span class="text-muted">-</span>
          </template>
        </template>

        <template #cell(milestone)="{ item }">
          <span>{{ formatMilestone(item) }}</span>
        </template>

        <template #cell(scheduled_date)="{ item }">
          <span>{{ formatDate(item.scheduled_date) }}</span>
        </template>

        <template #cell(amount_minor)="{ item }">
          <template v-if="item.amount_minor">
            <span>{{ formatCurrency(item.amount_minor) }}</span>
          </template>
          <template v-else>
            <span class="text-muted">--</span>
          </template>
        </template>
      </b-table>

      <div v-else class="upcoming-awards-table__empty">
        <p class="text-muted">No upcoming awards in the next 30 days.</p>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import moment from 'moment';
import AppLoading from '@/components/AppLoading.vue';
import { toCurrency } from '@/utils';
import State from '@/state/State';

const props = defineProps({
  awards: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const UPCOMING_AWARDS_FIELDS = [
  {
    key: 'full_name',
    label: 'Employee',
  },
  {
    key: 'work_email',
    label: 'Email',
  },
  {
    key: 'employee_code',
    label: 'Employee No.',
  },
  {
    key: 'scope',
    label: 'Scope',
  },
  {
    key: 'milestone',
    label: 'Milestone',
  },
  {
    key: 'scheduled_date',
    label: 'Scheduled date',
  },
  {
    key: 'amount_minor',
    label: 'Value',
  },
];

const currency = computed(() => State.state?.company?.default_currency);
const divisions = computed(() => State.state?.divisions || []);

// Create a map of division_id to division name
const divisionMap = computed(() => {
  const map = {};
  for (const div of divisions.value) {
    map[div.division_id] = div.name;
  }
  return map;
});

// Calculate total value
const totalValueMinor = computed(() => {
  return props.awards.reduce((sum, award) => sum + (award.amount_minor || 0), 0);
});

const formattedTotalValue = computed(() => {
  return toCurrency(totalValueMinor.value, currency.value);
});

// Enrich awards with division names
const enrichedAwards = computed(() => {
  return props.awards.map(award => {
    const enabledDivisions = award.enabled_divisions || [];
    const divisionNames = enabledDivisions.map(divId => divisionMap.value[divId]).filter(name => name);

    return {
      ...award,
      divisionNames,
    };
  });
});

const formatCurrency = value => {
  return toCurrency(value, currency.value);
};

const formatDate = dateStr => {
  if (!dateStr) {
    return '-';
  }
  return moment(dateStr).format('DD MMM YYYY');
};

const formatMilestone = item => {
  if (item.event_type === 'BIRTHDAY') {
    return 'Birthday';
  }
  if (item.event_type === 'ANNIVERSARY') {
    // Extract year from program_name if available (e.g., "1 Year milestone")
    const match = item.program_name?.match(/(\d+)\s*year/i);
    if (match) {
      return `${match[1]} year anniversary`;
    }
    return 'Anniversary';
  }
  return item.event_type || '-';
};
</script>

<style scoped lang="scss">
.upcoming-awards-table {
  &__summary {
    padding: 1.5rem;
    margin-bottom: 1rem;
  }

  &__total {
    font-size: 2.5rem;
    font-weight: 600;
    color: var(--palette-color-default);
    line-height: 1.2;
  }

  &__description {
    font-size: 0.875rem;
    color: var(--palette-color-default-lighten-40);
    margin-top: 0.25rem;
  }

  &__table {
    :deep(thead th) {
      font-weight: 500;
      font-size: 0.875rem;
      color: var(--palette-color-default-lighten-40);
      border-bottom: 1px solid var(--palette-color-default-lighten-90);
      padding: 0.75rem 1rem;
    }

    :deep(tbody td) {
      padding: 1rem;
      vertical-align: middle;
      border-bottom: 1px solid var(--palette-color-default-lighten-90);
    }

    :deep(tbody tr:hover) {
      background-color: var(--palette-color-default-lighten-95);
    }
  }

  &__employee {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  &__avatar {
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 50%;
    background-color: var(--palette-color-default-lighten-80);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: var(--palette-color-default-lighten-40);
    font-size: 1.5rem;
  }

  &__name {
    font-weight: 500;
  }

  &__email {
    color: var(--palette-color-default-lighten-30);
  }

  &__empty {
    padding: 3rem;
    text-align: center;
  }
}
</style>
